import { useHistory, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  buildRecentSearchDisplayPayload,
  convertYearKeyToRange,
  decodeLocationV2Entry,
  expandIndustryParentToChildren,
  getFilterValuesWithExtraValues,
  getV2SavedTabPageSize,
  LEADS_PER_PAGE,
  suggestionToLocationItem,
  V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY,
  appendAiFiltersToUrl,
  isPositiveCompanyId,
} from '../../helpers/helper';
import {
  DEPARTMENT_DATA,
  INDUSTRY_DATA,
  LOCATION_DATA,
} from '../../helpers/filter-data';
import {
  fetchAISearchResult,
  getLeadsV2,
  getLeadsV2Count,
  getSavedLeads,
  getSavedLeadsV2Count,
  getCompanyListsAPI,
} from '../../helpers/leads.api';
import {
  GetLeadsV2CountResponse,
  LeadFinderSecondaryTab,
  LeadFinderPrimaryTab,
  LeadRevealType,
  SavedTabFilters,
  LeadFinderListTab,
  LeadFinderListVisibility,
} from '../../types';
import { RootState } from '../../../../store/root-reducer';
import toaster, { Theme } from '../../../../shared/toaster';
import { dateToString } from '../../../../shared/functions/date';
import { ExceptionTypes } from '../../../../shared/types';
import {
  setAiSearchPopupInputValue,
  setAiSearchPopupSubmitted,
  setIsAiSearchInProgress,
} from '../../v2-lead-slice';
import { redirectWithoutRefresh } from '../../../../shared/utils';
import { StorageUtil } from '../../../../shared/utils/storage';

// Cached whole-database lead total shown in the default (no-filter) view so we
// can skip the expensive { tab: 'total' } count query. Update periodically.
const DEFAULT_TOTAL_LEADS_COUNT = 872130722;

// netNew for the cached default view = cachedTotal - savedCount.
// Unknown saved -> undefined (New badge shows skeleton). Negative -> cachedTotal.
const deriveDefaultNetNew = (
  saved: number | undefined,
  total: number,
): number | undefined => {
  if (saved == null) return undefined;
  const net = total - saved;
  return net < 0 ? total : net;
};

export const useLeadFinder = (
  activeSubTab: LeadFinderSecondaryTab,
  setSavedFilters: React.Dispatch<React.SetStateAction<SavedTabFilters>>,
  setEmailRevealingLeads: React.Dispatch<
    React.SetStateAction<Record<number, boolean>>
  >,
  setEmailPhoneRevealingLeads: React.Dispatch<
    React.SetStateAction<Record<number, boolean>>
  >,
  shouldBlockAccess = false,
  activeLeadType?: LeadFinderPrimaryTab,
  skipUrlPushForBulkSearchRef?: React.MutableRefObject<boolean>,
) => {
  const history = useHistory();
  const location = useLocation();

  // ** Redux
  const dispatch = useDispatch();
  const userEmail = useSelector((state: RootState) => state?.home?.email);
  const isEmailVerified = useSelector(
    (state: RootState) => state?.home?.verified,
  );
  const {
    aiSeachPopupSubmitted,
    aiSearchInputValue,
    isAiSearchInProgress,
  } = useSelector((state: RootState) => state?.leadFinderV2);

  const queryParams = new URLSearchParams(location?.search);
  const isSavedSubTab = activeSubTab === LeadFinderSecondaryTab.SAVED;

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);
  const [isLoadingSavedCounts, setIsLoadingSavedCounts] = useState(false);
  const [urlState, setUrlState] = useState<any>();
  // Pagination state - stored in React state only, not in URL
  const [apiParam, setApiParam] = useState<any>({
    page: 1,
  });
  const [savedApiParam, setSavedApiParam] = useState<any>({
    start: 1,
    take: getV2SavedTabPageSize(),
  });
  const [leadsData, setLeadsData] = useState({
    profiles: [],
    pagination: null,
  });
  const [countsData, setCountsData] = useState<
    GetLeadsV2CountResponse['payload'] | null
  >(null);
  const [countsError, setCountsError] = useState<boolean>(false);
  const [peopleListsData, setPeopleListsData] = useState<any[]>([]);
  const [companyListsData, setCompanyListsData] = useState<any[]>([]);
  const [isLoadingPeopleLists, setIsLoadingPeopleLists] = useState(false);
  const [isLoadingCompanyLists, setIsLoadingCompanyLists] = useState(false);
  const previousSearchParamsRef = useRef<string>('');
  const previousSubTabRef = useRef<LeadFinderSecondaryTab | null>(null);
  const previousActiveLeadTypeRef = useRef<LeadFinderPrimaryTab | undefined>(
    undefined,
  );
  const countsDataRef = useRef<GetLeadsV2CountResponse['payload'] | null>(null);
  const previousSavedFiltersRef = useRef<SavedTabFilters | null>(null);
  const aiUrlUpdateRef = useRef(false);
  const skipNextCountCallRef = useRef(false);
  const lastAppliedNonSavedFiltersRef = useRef<Record<string, any> | null>(
    null,
  );
  const currentRequestIdRef = useRef(0);
  const currentRequestKeyRef = useRef<string>('');
  const activeSubTabRef = useRef(activeSubTab);
  const showingFilteredCountsRef = useRef(false);

  // For V2 location & company_hq_location filter: split by comma and take first part for API payload
  // Full values remain in URL and chips
  const splitLocationValue = (value: string): string => {
    if (value.includes(',')) {
      return value.split(',')[0].trim();
    }
    return value;
  };

  const handleGeneratePayload = useCallback(
    (source?: string | URLSearchParams) => {
      const queryParamsUpdated =
        source === undefined
          ? new URLSearchParams(window.location?.search)
          : new URLSearchParams(source ?? '');
      const paramArray = Array.from(queryParamsUpdated.entries());

      const legacyLinkValues = queryParamsUpdated.getAll('link');

      if (legacyLinkValues.length) {
        queryParamsUpdated.delete('link');

        legacyLinkValues.forEach((value) => {
          queryParamsUpdated.append('linkedin_url', value);
        });

        history.replace({
          search: queryParamsUpdated.toString(),
        });
      }
      const payload: any = {};
      const savedFilters: SavedTabFilters = {
        createdDate: [],
        search: '',
        tags: [],
        listIds: { includes: [], excludes: [] },
      };

      // Helper to get includes/excludes structure
      const getIncludesExcludes = (
        includeKey: string,
        excludeKey?: string,
        options: {
          exactMatch?: boolean;
          shouldSearchPast?: boolean;
          isUnknown?: number;
        } = {},
      ) => {
        // When isUnknown = 1, only send the isUnknown flag without filter values
        if (options.isUnknown === 1) {
          return { isUnknown: 1 };
        }

        const includes = queryParamsUpdated.getAll(includeKey);
        const excludes = excludeKey
          ? queryParamsUpdated.getAll(excludeKey)
          : [];

        // When isUnknown = 0 (is known) and no value, only send the isUnknown flag
        if (
          options.isUnknown === 0 &&
          includes.length === 0 &&
          excludes.length === 0
        ) {
          return { isUnknown: 0 };
        }

        // Only return data if there are actual values
        // Don't return empty arrays just because flags exist
        if (
          includes.length === 0 &&
          excludes.length === 0 &&
          options.isUnknown === undefined
        ) {
          return null;
        }

        const result: any = {};

        if (includes.length > 0) {
          result.includes = includes.map((v) => v.replace(/"/g, ''));
        } else {
          result.includes = [];
        }

        if (excludes.length > 0) {
          result.excludes = excludes.map((v) => v.replace(/"/g, ''));
        } else if (excludeKey) {
          result.excludes = [];
        }

        if (options.exactMatch !== undefined) {
          result.exactMatch = options.exactMatch ? 1 : 0;
        }

        if (options.shouldSearchPast === true) {
          result.shouldSearchPast = 1;
        }

        if (options.isUnknown !== undefined) {
          result.isUnknown = options.isUnknown;
        }

        return result;
      };

      // Helper function to convert ISO date string to yyyy-mm-dd format
      const convertISODateToYYYYMMDD = (dateString: string): string => {
        try {
          // Handle URL-encoded strings
          const decodedString = decodeURIComponent(dateString);
          // Try to parse as ISO date
          const date = new Date(decodedString);
          if (!Number.isNaN(date.getTime())) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          }
        } catch (e) {
          // If parsing fails, return original string
        }
        return dateString;
      };

      const getMinMaxRange = (key: string, hasIsUnknownFlag = false) => {
        const value = queryParamsUpdated.get(key);
        const unknownValue = hasIsUnknownFlag
          ? queryParamsUpdated.get(`${key}_isUnknown`)
          : undefined;

        // When isUnknown = 1, only send the isUnknown flag without filter values
        if (unknownValue === '1') {
          return { isUnknown: 1 };
        }

        // When isUnknown = 0 (is known) and no value, only send the isUnknown flag
        if (unknownValue === '0' && !value) {
          return { isUnknown: 0 };
        }

        if (!value && unknownValue === undefined) {
          return null;
        }

        if (value) {
          // For company_funding_date, convert ISO date strings to yyyy-mm-dd format
          if (key === 'company_funding_date') {
            // Handle "all time" special case - pass exists: true instead of min/max
            if (value === 'all time') {
              const result: any = { exists: true };
              if (unknownValue !== undefined) {
                result.isUnknown = unknownValue === '1' ? 1 : 0;
              }
              return result;
            }

            // Check if it's a date range (contains " - ")
            if (value.includes(' - ')) {
              const [startDateStr, endDateStr] = value.split(' - ');
              const formattedStart = convertISODateToYYYYMMDD(startDateStr);
              const formattedEnd = convertISODateToYYYYMMDD(endDateStr);
              // Ensure min is the earlier date and max is the later date
              const startDateObj = new Date(formattedStart);
              const endDateObj = new Date(formattedEnd);
              const minDate =
                startDateObj <= endDateObj ? formattedStart : formattedEnd;
              const maxDate =
                startDateObj <= endDateObj ? formattedEnd : formattedStart;
              const result: any = { min: minDate, max: maxDate };
              if (unknownValue !== undefined) {
                result.isUnknown = unknownValue === '1' ? 1 : 0;
              }
              return result;
            }

            // If it's already in yyyy-mm-dd format (custom date), use as is
            const datePattern = /^\d{4}-\d{2}-\d{2}$/;
            if (datePattern.test(value)) {
              const result: any = { min: value, max: value };
              if (unknownValue !== undefined) {
                result.isUnknown = unknownValue === '1' ? 1 : 0;
              }
              return result;
            }
          }

          // Handle filters with less than (<) and greater than (+) formats
          const filtersWithSpecialFormats = [
            'company_funding_amount',
            'company_annual_revenue',
            'company_size',
            'projected_base_salary',
            'linkedin_followers_count',
            'linkedin_connection_count',
          ];

          if (filtersWithSpecialFormats.includes(key)) {
            // Handle less than format (e.g., "<10000000" or "<50000")
            if (value.startsWith('<')) {
              const maxValue = value.replace('<', '').trim();
              const result: any = { min: '', max: maxValue };
              if (unknownValue !== undefined) {
                result.isUnknown = unknownValue === '1' ? 1 : 0;
              }
              return result;
            }

            // Handle greater than format (e.g., "5000000000+" or "25001+")
            if (value.endsWith('+')) {
              const minValue = value.replace('+', '').trim();
              const result: any = { min: minValue, max: '' };
              if (unknownValue !== undefined) {
                result.isUnknown = unknownValue === '1' ? 1 : 0;
              }
              return result;
            }
          }

          // For company_founded_year, convert relative keys to actual year range
          let processedValue = value;
          if (key === 'company_founded_year') {
            const convertedValue = convertYearKeyToRange(value);
            if (convertedValue) {
              processedValue = convertedValue;
            }
          }

          // For current_role_tenure, current_company_experience, and total_experience, handle "10+" format
          if (
            [
              'current_role_tenure',
              'current_company_experience',
              'total_experience',
            ].includes(key) &&
            value.endsWith('+')
          ) {
            const minValue = value.replace('+', '').trim();
            const result: any = { min: minValue, max: '' };
            if (unknownValue !== undefined) {
              result.isUnknown = unknownValue === '1' ? 1 : 0;
            }
            return result;
          }

          // Parse dropdown value (e.g., "1-5" or "10-100" or "2021-2024")
          const [min, max] = processedValue.split('-');
          const result: any = { min, max };

          if (unknownValue !== undefined) {
            result.isUnknown = unknownValue === '1' ? 1 : 0;
          }

          return result;
        }

        return null;
      };

      // Process all parameters
      paramArray.forEach(([key, value]) => {
        switch (key) {
          // Saved tab filters - 'search' is for saved tab search
          case 'search': {
            const searchValue = queryParamsUpdated.get('search');
            if (searchValue) {
              if (isSavedSubTab) {
                payload.search = [searchValue];
              }
              savedFilters.search = searchValue;
            } else {
              savedFilters.search = '';
            }
            break;
          }
          // Keywords filter for people/company tabs - uses includes structure only
          case 'keywords': {
            const keywordIncludes = queryParamsUpdated
              .getAll('keywords')
              .filter(Boolean);

            if (keywordIncludes.length > 0) {
              payload.keywords = {
                includes: keywordIncludes.map((v) => v.replace(/"/g, '')),
              };
            }
            break;
          }
          case 'tags':
            if (value) {
              if (isSavedSubTab) {
                payload.tags = value;
              }
              savedFilters.tags = value
                .split(',')
                .map((tag) => Number(tag.trim()));
            }
            break;
          case 'createdDate':
            if (value) {
              const [formattedStartDate, formattedEndDate] = value.split('-');
              const startDate = new Date(formattedStartDate);
              const endDate = new Date(formattedEndDate);
              endDate.setHours(23, 59, 59, 999);
              const dateObjects = [
                encodeURIComponent(dateToString(startDate)),
                encodeURIComponent(dateToString(endDate)),
              ];
              if (isSavedSubTab) {
                payload.createdDate = dateObjects.join(',');
              }
              savedFilters.createdDate = [startDate, endDate];
            }
            break;

          // Skip pagination and utility params
          case 'start':
          case 'take':
          case 'page':
          case 'purgeCache':
            // These are handled separately
            break;

          // Skip exclude keys and special flags (handled with their parent keys)
          case 'excludeLocation':
          case 'excludeDepartment':
          case 'excludeSkills':
          case 'excludeJob_title':
          case 'excludeCompany_name':
          case 'excludeCompany_industry':
          case 'excludeCompany_hq_location':
          case 'excludeManagement_level':
          case 'excludeMajor':
          case 'excludeSchool':
          case 'excludeDegree':
          case 'excludeEducation_degrees':
          case 'full_name_exactMatch':
          case 'location_exactMatch':
          case 'job_title_exactMatch':
          case 'job_title_shouldSearchPast':
          case 'job_title_isUnknown':
          case 'department_shouldSearchPast':
          case 'skills_exactMatch':
          case 'company_ids_shouldSearchPast':
          case 'company_name_shouldSearchPast':
          case 'company_name_exactMatch':
          case 'company_industry_exactMatch':
          case 'company_hq_location_exactMatch':
          case 'jobs_changed_within_isUnknown':
          case 'total_experience_isUnknown':
          case 'projected_base_salary_isUnknown':
          case 'company_size_isUnknown':
          case 'company_annual_revenue_isUnknown':
          case 'company_funding_amount_isUnknown':
          case 'company_funding_date_isUnknown':
          case 'company_founded_year_isUnknown':
          case 'education_degrees_exactMatch':
          case 'people_list':
          case 'excludePeople_list':
          case 'company_list':
          case 'excludeCompany_list':
            // Skip - these are handled with their main keys
            break;

          case 'excludeProspects':
            if (value === 'true') {
              if (isSavedSubTab) {
                payload.excludeProspects = true;
              }
              savedFilters.excludeProspects = true;
            }
            break;

          case 'emailStatus':
            if (value) {
              const allowedEmailStatus = ['valid', 'risky', 'bad'];
              const parsedEmailStatus = value
                .split(',')
                .map((s) => s.trim())
                .filter((s) => allowedEmailStatus.includes(s));
              if (parsedEmailStatus.length > 0) {
                payload.emailStatus = parsedEmailStatus;
                savedFilters.emailStatus = parsedEmailStatus as any;
              }
            }
            break;

          default:
            // Will be handled in specific processing below
            break;
        }
      });

      // Process specific filters with new structure
      // 1. Full Name with includes/exactMatch
      const fullNameData = getIncludesExcludes('full_name', undefined, {
        exactMatch: queryParamsUpdated.get('full_name_exactMatch') === '1',
      });
      if (fullNameData && fullNameData.includes.length > 0) {
        payload.full_name = {
          includes: fullNameData.includes,
          exactMatch: fullNameData.exactMatch,
        };
      }

      // 2. Location with includes/excludes/exactMatch
      const locationExactMatch =
        queryParamsUpdated.get('location_exactMatch') === '1' ||
        queryParamsUpdated.get('location_v2_exactMatch') === '1';
      const locationData = getIncludesExcludes('location', 'excludeLocation', {
        exactMatch: locationExactMatch,
      });
      if (locationData) {
        // Process location values with LOCATION_DATA
        const processedIncludes =
          locationData.includes.length > 0
            ? getFilterValuesWithExtraValues(
              locationData.includes,
              LOCATION_DATA,
              true,
            )
            : [];
        const processedExcludes =
          locationData.excludes.length > 0
            ? getFilterValuesWithExtraValues(
              locationData.excludes,
              LOCATION_DATA,
              true,
            )
            : [];

        payload.location = {
          includes: processedIncludes.map(splitLocationValue),
          excludes: processedExcludes.map(splitLocationValue),
          exactMatch: locationData.exactMatch,
        };
      }

      // 2b. Location v2 — structured picks from the suggestion dropdown
      const locationV2IncludesRaw = queryParamsUpdated.getAll('location_v2');
      const locationV2ExcludesRaw = queryParamsUpdated.getAll(
        'excludeLocation_v2',
      );
      const locationV2Includes = locationV2IncludesRaw
        .map((raw) => decodeLocationV2Entry(raw))
        .filter((e): e is NonNullable<typeof e> => e !== null)
        .map((e) => suggestionToLocationItem(e));
      const locationV2Excludes = locationV2ExcludesRaw
        .map((raw) => decodeLocationV2Entry(raw))
        .filter((e): e is NonNullable<typeof e> => e !== null)
        .map((e) => suggestionToLocationItem(e));
      if (locationV2Includes.length > 0 || locationV2Excludes.length > 0) {
        payload.location_v2 = {
          includes: locationV2Includes,
          excludes: locationV2Excludes,
          exactMatch: locationExactMatch ? 1 : 0,
        };
      }

      // 3. Job Title with includes/excludes/exactMatch/isUnknown/shouldSearchPast
      let jobTitleIsUnknown: number | undefined;
      if (queryParamsUpdated.has('job_title_isUnknown')) {
        jobTitleIsUnknown =
          queryParamsUpdated.get('job_title_isUnknown') === '1' ? 1 : 0;
      }
      const jobTitleData = getIncludesExcludes(
        'job_title',
        'excludeJob_title',
        {
          exactMatch: queryParamsUpdated.get('job_title_exactMatch') === '1',
          isUnknown: jobTitleIsUnknown,
          shouldSearchPast:
            queryParamsUpdated.get('job_title_shouldSearchPast') === '1',
        },
      );
      if (jobTitleData) {
        payload.job_title = jobTitleData;
      }

      // 4. Department with includes/excludes/shouldSearchPast
      const departmentData = getIncludesExcludes(
        'department',
        'excludeDepartment',
        {
          shouldSearchPast:
            queryParamsUpdated.get('department_shouldSearchPast') === '1',
        },
      );
      if (departmentData) {
        // Process department values with DEPARTMENT_DATA
        const processedIncludes =
          departmentData.includes.length > 0
            ? getFilterValuesWithExtraValues(
              departmentData.includes,
              DEPARTMENT_DATA,
            )
            : [];
        const processedExcludes =
          departmentData.excludes.length > 0
            ? getFilterValuesWithExtraValues(
              departmentData.excludes,
              DEPARTMENT_DATA,
            )
            : [];

        payload.department = {
          includes: processedIncludes,
          excludes: processedExcludes,
          shouldSearchPast: departmentData.shouldSearchPast,
        };
      }

      // 5. Management Level with includes/excludes
      const managementLevelData = getIncludesExcludes(
        'management_level',
        'excludeManagement_level',
      );
      if (managementLevelData) {
        payload.management_level = {
          includes: managementLevelData.includes,
          excludes: managementLevelData.excludes,
        };
      }

      // 6. Decision Maker (radio button - 0/1)
      if (queryParamsUpdated.has('decision_maker')) {
        const value = queryParamsUpdated.get('decision_maker');
        payload.decision_maker = value === '1' ? 1 : 0;
      }

      // 7. Jobs Changed Within (dropdown with min/max/isUnknown)
      const jobsChangedData = getMinMaxRange(
        'jobs_changed_within',
        queryParamsUpdated.has('jobs_changed_within_isUnknown'),
      );
      if (jobsChangedData) {
        payload.jobs_changed_within = jobsChangedData;
      }

      // 8. Skills with includes/excludes/exactMatch
      const skillsData = getIncludesExcludes('skills', 'excludeSkills', {
        exactMatch: queryParamsUpdated.get('skills_exactMatch') === '1',
      });
      if (skillsData) {
        payload.skills = skillsData;
      }

      // 9. Current Company Experience (dropdown with min/max)
      const currentCompanyExpData = getMinMaxRange(
        'current_company_experience',
      );
      if (currentCompanyExpData) {
        payload.current_company_experience = currentCompanyExpData;
      }

      // 10. Current Role Tenure (dropdown with min/max)
      const currentRoleTenureData = getMinMaxRange('current_role_tenure');
      if (currentRoleTenureData) {
        payload.current_role_tenure = currentRoleTenureData;
      }

      // 11. Total Experience (dropdown with min/max/isUnknown)
      const totalExpData = getMinMaxRange(
        'total_experience',
        queryParamsUpdated.has('total_experience_isUnknown'),
      );
      if (totalExpData) {
        payload.total_experience = totalExpData;
      }

      // 12. Projected Base Salary (dropdown with min/max/isUnknown)
      const salaryData = getMinMaxRange(
        'projected_base_salary',
        queryParamsUpdated.has('projected_base_salary_isUnknown'),
      );
      if (salaryData) {
        payload.projected_base_salary = salaryData;
      }

      // 13. Company Industry with includes/excludes
      const industryData = getIncludesExcludes(
        'company_industry',
        'excludeCompany_industry',
      );
      if (industryData) {
        // Process industry values with INDUSTRY_DATA
        // When a parent industry is selected, expand to all children values
        const processedIncludes =
          industryData.includes.length > 0
            ? expandIndustryParentToChildren(
              industryData.includes,
              INDUSTRY_DATA,
            )
            : [];
        const processedExcludes =
          industryData.excludes.length > 0
            ? expandIndustryParentToChildren(
              industryData.excludes,
              INDUSTRY_DATA,
            )
            : [];

        payload.company_industry = {
          includes: processedIncludes,
          excludes: processedExcludes,
        };
      }

      // 14. Company Name with includes/excludes/exactMatch
      // Dropdown selections have numeric IDs -> company_ids
      // Custom text entries (non-numeric) -> company_name
      const companyIdsData = getIncludesExcludes(
        'company_ids',
        'excludeCompany_ids',
        {
          shouldSearchPast:
            queryParamsUpdated.get('company_ids_shouldSearchPast') === '1',
        },
      );

      const customNameIncludes: string[] = [];
      const customNameExcludes: string[] = [];

      if (companyIdsData) {
        if (companyIdsData.includes) {
          const numericIncludes: number[] = [];
          companyIdsData.includes.forEach((val: string) => {
            if (isPositiveCompanyId(val)) {
              numericIncludes.push(Number(val));
            } else {
              customNameIncludes.push(val);
            }
          });
          companyIdsData.includes = numericIncludes;
        }
        if (companyIdsData.excludes) {
          const numericExcludes: number[] = [];
          companyIdsData.excludes.forEach((val: string) => {
            if (isPositiveCompanyId(val)) {
              numericExcludes.push(Number(val));
            } else {
              customNameExcludes.push(val);
            }
          });
          companyIdsData.excludes = numericExcludes;
        }
        if (
          companyIdsData.includes?.length > 0 ||
          companyIdsData.excludes?.length > 0
        ) {
          payload.company_ids = companyIdsData;
        }
      }

      // Company Name (from bulk search + custom text entries) with includes/excludes/exactMatch
      const companyNameData = getIncludesExcludes(
        'company_name',
        'excludeCompany_name',
        {
          shouldSearchPast:
            queryParamsUpdated.get('company_name_shouldSearchPast') === '1',
        },
      );

      // Merge custom text entries from company_ids into company_name
      if (customNameIncludes.length > 0 || customNameExcludes.length > 0) {
        const nameIncludes = [
          ...(companyNameData?.includes || []),
          ...customNameIncludes,
        ];
        const nameExcludes = [
          ...(companyNameData?.excludes || []),
          ...customNameExcludes,
        ];
        const mergedShouldSearchPast =
          companyNameData?.shouldSearchPast === 1 ||
          queryParamsUpdated.get('company_name_shouldSearchPast') === '1' ||
          queryParamsUpdated.get('company_ids_shouldSearchPast') === '1';
        payload.company_name = {
          ...(companyNameData || {}),
          ...(nameIncludes.length > 0 ? { includes: nameIncludes } : {}),
          ...(nameExcludes.length > 0 ? { excludes: nameExcludes } : {}),
          ...(mergedShouldSearchPast && { shouldSearchPast: 1 }),
        };
      } else if (companyNameData) {
        payload.company_name = companyNameData;
      }

      // Company Domain with includes/excludes
      const companyDomainData = getIncludesExcludes(
        'company_domain',
        'excludeCompany_domain',
      );
      if (companyDomainData) {
        payload.company_domain = companyDomainData;
      }

      // 15. Is B2B (radio button - 0/1)
      if (queryParamsUpdated.has('is_b2b')) {
        const value = queryParamsUpdated.get('is_b2b');
        payload.is_b2b = value === '1' ? 1 : 0;
      }

      // 16. Company Size (dropdown with min/max/isUnknown)
      const companySizeData = getMinMaxRange(
        'company_size',
        queryParamsUpdated.has('company_size_isUnknown'),
      );
      if (companySizeData) {
        payload.company_size = companySizeData;
      }

      // 17. Company HQ Location with includes/excludes
      const companyHqLocationExactMatch =
        queryParamsUpdated.get('company_hq_location_exactMatch') === '1' ||
        queryParamsUpdated.get('company_hq_location_v2_exactMatch') === '1';
      const companyHqLocationData = getIncludesExcludes(
        'company_hq_location',
        'excludeCompany_hq_location',
        {
          exactMatch: companyHqLocationExactMatch,
        },
      );
      if (companyHqLocationData) {
        // Process location values with LOCATION_DATA
        const processedIncludes =
          companyHqLocationData.includes.length > 0
            ? getFilterValuesWithExtraValues(
              companyHqLocationData.includes,
              LOCATION_DATA,
              true,
            )
            : [];

        const processedExcludes =
          companyHqLocationData.excludes.length > 0
            ? getFilterValuesWithExtraValues(
              companyHqLocationData.excludes,
              LOCATION_DATA,
              true,
            )
            : [];

        payload.company_hq_location = {
          includes: processedIncludes.map(splitLocationValue),
          excludes: processedExcludes.map(splitLocationValue),
          exactMatch: companyHqLocationData.exactMatch,
        };
      }

      // 17b. Company HQ Location v2 — structured picks from suggestion dropdown
      const companyHqV2IncludesRaw = queryParamsUpdated.getAll(
        'company_hq_location_v2',
      );
      const companyHqV2ExcludesRaw = queryParamsUpdated.getAll(
        'excludeCompany_hq_location_v2',
      );
      const companyHqV2Includes = companyHqV2IncludesRaw
        .map((raw) => decodeLocationV2Entry(raw))
        .filter((e): e is NonNullable<typeof e> => e !== null)
        .map((e) => suggestionToLocationItem(e));
      const companyHqV2Excludes = companyHqV2ExcludesRaw
        .map((raw) => decodeLocationV2Entry(raw))
        .filter((e): e is NonNullable<typeof e> => e !== null)
        .map((e) => suggestionToLocationItem(e));
      if (companyHqV2Includes.length > 0 || companyHqV2Excludes.length > 0) {
        payload.company_hq_location_v2 = {
          includes: companyHqV2Includes,
          excludes: companyHqV2Excludes,
          exactMatch: companyHqLocationExactMatch ? 1 : 0,
        };
      }

      // 18. Company Bootstraped (radio button - 0/1)
      if (queryParamsUpdated.has('company_bootstraped')) {
        const value = queryParamsUpdated.get('company_bootstraped');
        payload.company_bootstraped = value === '1' ? 1 : 0;
      }

      // 19. Company Annual Revenue (dropdown with min/max/isUnknown)
      const revenueData = getMinMaxRange(
        'company_annual_revenue',
        queryParamsUpdated.has('company_annual_revenue_isUnknown'),
      );
      if (revenueData) {
        payload.company_annual_revenue = revenueData;
      }

      // 20. Company Funding Amount (dropdown with min/max/isUnknown)
      const fundingAmountData = getMinMaxRange(
        'company_funding_amount',
        queryParamsUpdated.has('company_funding_amount_isUnknown'),
      );
      if (fundingAmountData) {
        payload.company_funding_amount = fundingAmountData;
      }

      // 21. Company Funding Date (dropdown with min/max/isUnknown)
      const fundingDateData = getMinMaxRange(
        'company_funding_date',
        queryParamsUpdated.has('company_funding_date_isUnknown'),
      );
      if (fundingDateData) {
        payload.company_funding_date = fundingDateData;
      }

      // 22. Company Founded Year (dropdown with min/max/isUnknown)
      const foundedYearData = getMinMaxRange(
        'company_founded_year',
        queryParamsUpdated.has('company_founded_year_isUnknown'),
      );
      if (foundedYearData) {
        payload.company_founded_year = foundedYearData;
      }

      // 23. Education Degrees with includes/excludes/exactMatch
      const degreesData = getIncludesExcludes(
        'education_degrees',
        'excludeEducation_degrees',
      );
      if (degreesData) {
        payload.education_degrees = degreesData;
      }

      // 24. Major with includes/excludes
      const majorData = getIncludesExcludes('major', 'excludeMajor');
      if (majorData) {
        payload.major = majorData;
      }

      // 25. School with includes/excludes
      const schoolData = getIncludesExcludes('school', 'excludeSchool');
      if (schoolData) {
        payload.school = schoolData;
      }

      // 26. LinkedIn URL with includes (multiple values)
      const linkedinUrlData = getIncludesExcludes('linkedin_url');
      if (linkedinUrlData && linkedinUrlData.includes.length > 0) {
        payload.linkedin_url = linkedinUrlData.includes;
      }

      // 27. LinkedIn Connection Count (dropdown with min/max)
      const linkedinConnectionData = getMinMaxRange(
        'linkedin_connection_count',
      );
      if (linkedinConnectionData) {
        payload.linkedin_connection_count = linkedinConnectionData;
      }

      // 28. LinkedIn Followers Count (dropdown with min/max)
      const linkedinFollowersData = getMinMaxRange('linkedin_followers_count');
      if (linkedinFollowersData) {
        payload.linkedin_followers_count = linkedinFollowersData;
      }

      // 29. Social Link with includes
      const socialLinkData = getIncludesExcludes('social_link');
      if (socialLinkData && socialLinkData.includes.length > 0) {
        payload.social_link = {
          includes: socialLinkData.includes,
        };
      }

      // 30. Keywords with includes only (already handled in switch case above)
      // Only process if not already set from URL params
      if (!payload.keywords) {
        const keywordsData = getIncludesExcludes('keywords');
        if (keywordsData && keywordsData.includes.length > 0) {
          payload.keywords = {
            includes: keywordsData.includes,
          };
        }
      }

      // 31. Certifications with includes
      const certificationsData = getIncludesExcludes('certifications');
      if (certificationsData && certificationsData.includes.length > 0) {
        payload.certifications = {
          includes: certificationsData.includes,
        };
      }

      // 32. Limit Per Company - convert to number and map to limitPerCompany
      if (queryParamsUpdated.has('limit')) {
        const limitValue = queryParamsUpdated.get('limit');
        const limitNum = parseInt(limitValue, 10);
        if (!Number.isNaN(limitNum) && limitNum > 0) {
          payload.limitPerCompany = limitNum;
        }
      }

      // People List (include/exclude)
      const peopleListIncludes = queryParamsUpdated.getAll('people_list');
      const peopleListExcludes = queryParamsUpdated.getAll(
        'excludePeople_list',
      );
      const peopleList: any = {};
      if (peopleListIncludes.length > 0) {
        const includesRaw = peopleListIncludes
          .flatMap((entry) => (entry ? entry.split(',') : []))
          .map((s) => s.replace(/"/g, '').trim())
          .filter(Boolean);
        const includes = includesRaw
          .map((id) => parseInt(id, 10))
          .filter((id) => !Number.isNaN(id) && id > 0);
        if (includes.length > 0) {
          peopleList.includes = includes;
        }
      }
      if (peopleListExcludes.length > 0) {
        const excludesRaw = peopleListExcludes
          .flatMap((entry) => (entry ? entry.split(',') : []))
          .map((s) => s.replace(/"/g, '').trim())
          .filter(Boolean);
        const excludes = excludesRaw
          .map((id) => parseInt(id, 10))
          .filter((id) => !Number.isNaN(id) && id > 0);
        if (excludes.length > 0) {
          peopleList.excludes = excludes;
        }
      }
      if (Object.keys(peopleList).length > 0) {
        payload.peopleList = peopleList;
        savedFilters.listIds = {
          includes: peopleList.includes || [],
          excludes: peopleList.excludes || [],
        };
      }

      // Company List (include/exclude)
      const companyListIncludes = queryParamsUpdated.getAll('company_list');
      const companyListExcludes = queryParamsUpdated.getAll(
        'excludeCompany_list',
      );
      const companyList: any = {};
      if (companyListIncludes.length > 0) {
        const includesRaw = companyListIncludes
          .flatMap((entry) => (entry ? entry.split(',') : []))
          .map((s) => s.replace(/"/g, '').trim())
          .filter(Boolean);
        const includes = includesRaw
          .map((id) => parseInt(id, 10))
          .filter((id) => !Number.isNaN(id) && id > 0);
        if (includes.length > 0) {
          companyList.includes = includes;
        }
      }
      if (companyListExcludes.length > 0) {
        const excludesRaw = companyListExcludes
          .flatMap((entry) => (entry ? entry.split(',') : []))
          .map((s) => s.replace(/"/g, '').trim())
          .filter(Boolean);
        const excludes = excludesRaw
          .map((id) => parseInt(id, 10))
          .filter((id) => !Number.isNaN(id) && id > 0);
        if (excludes.length > 0) {
          companyList.excludes = excludes;
        }
      }
      if (Object.keys(companyList).length > 0) {
        payload.companyList = companyList;
        savedFilters.listIds = {
          includes: companyList.includes || [],
          excludes: companyList.excludes || [],
        };
      }

      // Handle saved tab filters
      if (isSavedSubTab) {
        // Only update if values actually changed to prevent unnecessary re-renders
        const prevFilters = previousSavedFiltersRef.current;
        if (!prevFilters) {
          // First time, always set
          setSavedFilters(savedFilters);
          previousSavedFiltersRef.current = { ...savedFilters };
        } else {
          // Compare values to avoid unnecessary updates
          const currentTagsStr = JSON.stringify(
            (savedFilters.tags || []).sort(),
          );
          const prevTagsStr = JSON.stringify((prevFilters.tags || []).sort());
          const currentSearch = savedFilters.search || '';
          const prevSearch = prevFilters.search || '';
          const currentDateStr = JSON.stringify(savedFilters.createdDate || []);
          const prevDateStr = JSON.stringify(prevFilters.createdDate || []);

          const currentEmailStatusStr = JSON.stringify(
            (savedFilters.emailStatus || []).slice().sort(),
          );
          const prevEmailStatusStr = JSON.stringify(
            (prevFilters.emailStatus || []).slice().sort(),
          );

          if (
            currentTagsStr !== prevTagsStr ||
            currentSearch !== prevSearch ||
            currentDateStr !== prevDateStr ||
            savedFilters.excludeProspects !== prevFilters.excludeProspects ||
            currentEmailStatusStr !== prevEmailStatusStr
          ) {
            setSavedFilters(savedFilters);
            previousSavedFiltersRef.current = { ...savedFilters };
          }
        }
      }

      return payload;
    },
    [isSavedSubTab, setSavedFilters],
  );

  const handleGenerateAPIParam = (subTab: LeadFinderSecondaryTab) => {
    const filtersPayload = handleGeneratePayload();
    const paginationPayload =
      subTab === LeadFinderSecondaryTab.SAVED ? savedApiParam : apiParam;
    const payload = {
      ...paginationPayload,
      ...filtersPayload,
    };
    return payload;
  };

  const handleRecentSearch = (source?: URLSearchParams) => {
    const payload: any = source
      ? handleGeneratePayload(source)
      : handleGeneratePayload();
    const meaningfulParams = Object.keys(payload).filter(
      (key) => key !== 'start' && key !== 'take' && key !== 'page',
    );

    if (isSavedSubTab) {
      return;
    }

    // Check if URL has any params (excluding pagination)
    const urlParams = new URLSearchParams(location.search);
    urlParams.delete('page');
    urlParams.delete('start');
    urlParams.delete('take');
    const hasUrlParams = urlParams.toString().length > 0;

    if (meaningfulParams.length > 0) {
      if (!userEmail) {
        return;
      }

      const storedUrlsKey = V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY;
      const storedUrls = JSON.parse(StorageUtil.getItem(storedUrlsKey) || '{}');

      if (!storedUrls[userEmail]) {
        storedUrls[userEmail] = [];
      }

      // In bulk search mode, URL is empty — use just the pathname so the entry is saved
      const currentUrlWithParams = source
        ? window.location.pathname
        : window.location.pathname + window.location.search;
      const timestamp = new Date().toISOString();

      // For bulk entries, deduplicate by filter fingerprint — different filters = different entries
      const existingUrlIndex = source
        ? storedUrls[userEmail].findIndex(
          (urlData) => urlData?.pendingFiltersStr === source.toString(),
        )
        : storedUrls[userEmail].findIndex(
          (urlData) =>
            urlData?.url?.toLowerCase() ===
            currentUrlWithParams?.toLowerCase(),
        );

      const maxUrls = 4;
      const filterParams = source || new URLSearchParams(location.search);
      const displayPayload = buildRecentSearchDisplayPayload(
        payload,
        filterParams,
        false,
      );

      if (existingUrlIndex !== -1) {
        const updatedEntry = {
          url: currentUrlWithParams,
          timestamp,
          payload,
          displayPayload,
          ...(source ? { pendingFiltersStr: source.toString() } : {}),
        };

        storedUrls[userEmail].unshift(
          storedUrls[userEmail].splice(existingUrlIndex, 1)[0],
        );
        storedUrls[userEmail][0] = updatedEntry;

        storedUrls[userEmail] = storedUrls[userEmail].slice(0, maxUrls);
      } else {
        const urlData = {
          url: currentUrlWithParams,
          timestamp,
          payload,
          displayPayload,
          ...(source ? { pendingFiltersStr: source.toString() } : {}),
        };

        storedUrls[userEmail].unshift(urlData);

        storedUrls[userEmail] = storedUrls[userEmail].slice(0, maxUrls);
      }

      StorageUtil.setItem(storedUrlsKey, JSON.stringify(storedUrls));
    }

    if (hasUrlParams) {
      const paramArray = Array.from(queryParams.entries());

      paramArray.forEach(([key]) => {
        queryParams.delete(key);
      });

      history.push({
        search: queryParams.toString(),
      });
    }
  };

  const handleRouteChange = () => {
    const payload: any = handleGeneratePayload();
    const meaningfulParams = Object.keys(payload).filter(
      (key) => key !== 'start' && key !== 'take' && key !== 'page',
    );

    if (isSavedSubTab) {
      return;
    }

    if (meaningfulParams.length > 0) {
      if (!userEmail) {
        return;
      }

      const storedUrlsKey = V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY;
      const storedUrls = JSON.parse(StorageUtil.getItem(storedUrlsKey) || '{}');

      if (!storedUrls[userEmail]) {
        storedUrls[userEmail] = [];
      }

      const currentUrlWithParams = location.pathname + location.search;
      const timestamp = new Date().toISOString();

      const existingUrlIndex = storedUrls[userEmail].findIndex(
        (urlData) =>
          urlData?.url?.toLowerCase() === currentUrlWithParams?.toLowerCase(),
      );

      const maxUrls = 4;
      const displayPayload = buildRecentSearchDisplayPayload(
        payload,
        new URLSearchParams(location.search),
        false,
      );

      if (existingUrlIndex !== -1) {
        const updatedEntry = {
          url: currentUrlWithParams,
          timestamp,
          payload,
          displayPayload,
        };

        storedUrls[userEmail].unshift(
          storedUrls[userEmail].splice(existingUrlIndex, 1)[0],
        );
        storedUrls[userEmail][0] = updatedEntry;

        storedUrls[userEmail] = storedUrls[userEmail].slice(0, maxUrls);
      } else {
        const urlData = {
          url: currentUrlWithParams,
          timestamp,
          payload,
          displayPayload,
        };

        storedUrls[userEmail].unshift(urlData);

        storedUrls[userEmail] = storedUrls[userEmail].slice(0, maxUrls);
      }

      StorageUtil.setItem(storedUrlsKey, JSON.stringify(storedUrls));
    }
  };

  const handleAppendLead = (lead, preserveRevealState = false) => {
    const matchProfile = (profile) =>
      (lead?.id != null && profile?.id === lead?.id) ||
      (lead?.contactId != null && profile?.contactId === lead?.contactId);

    const indexOfProfileToReplace = leadsData?.profiles?.findIndex(
      matchProfile,
    );

    if (indexOfProfileToReplace !== -1) {
      setLeadsData((prevState) => ({
        ...prevState,
        profiles: (prevState.profiles || []).map((profile) => {
          const isCurrentLead = matchProfile(profile);
          const { tags } = profile;
          if (!isCurrentLead) return profile;
          const merged = { ...lead, tags };
          if (preserveRevealState) {
            merged.emails = profile.emails;
            merged.personalEmails = profile.personalEmails;
            merged.phones = profile.phones;
            merged.isRevealed = profile.isRevealed;
            merged.revealType = profile.revealType;
            merged.isPersonalEmailRevealed = profile.isPersonalEmailRevealed;
            merged.revealFeedback = profile.revealFeedback;
          }
          return merged;
        }),
      }));
    }
  };

  const updateUrlWithAiSearchParams = useCallback((filtersPayload: any) => {
    const currentUrl = new URL(window?.location?.href);
    currentUrl.search = '';
    appendAiFiltersToUrl(currentUrl, filtersPayload || {});

    // Mark that the upcoming location.search change is AI-originated so the
    // location effect skips re-fetching counts and search.
    aiUrlUpdateRef.current = true;
    redirectWithoutRefresh(currentUrl?.search?.toString());
  }, []);

  const saveAISearchToLocalStorage = (aiSearchInputValues: string) => {
    const aiLeadSearchHistory = JSON.parse(
      StorageUtil.getItem('aiV2SearchHistory') || '{}',
    );
    if (!Array.isArray(aiLeadSearchHistory[userEmail])) {
      aiLeadSearchHistory[userEmail] = [];
    }
    aiLeadSearchHistory[userEmail] = aiLeadSearchHistory[userEmail]?.filter(
      (entry) =>
        entry.aiSearchInputValues?.toLowerCase() !==
        aiSearchInputValues?.toLowerCase(),
    );
    aiLeadSearchHistory[userEmail]?.unshift({ aiSearchInputValues });

    aiLeadSearchHistory[userEmail] = aiLeadSearchHistory[userEmail]?.slice(
      0,
      4,
    );
    StorageUtil.setItem(
      'aiV2SearchHistory',
      JSON.stringify(aiLeadSearchHistory),
    );
  };

  const populateRevealingLeadsStateAfterSearch = (profiles: any[]) => {
    if (!profiles?.length) {
      return;
    }
    const revealingSearchEmailLeads = profiles.filter(
      (lead) => lead.isRevealing && lead?.revealType === LeadRevealType.Email,
    );
    const revealingSearchEmailPhoneLeads = profiles.filter(
      (lead) =>
        lead.isRevealing && lead?.revealType === LeadRevealType.EmailPhone,
    );
    const revealedLeads = profiles.filter((lead) => lead.isRevealed);
    // email
    const updateLoadingEmailLeads = [];
    updateLoadingEmailLeads.push(
      ...revealingSearchEmailLeads.map((lead) => [lead.id, true]),
    );
    updateLoadingEmailLeads.push(
      ...revealedLeads.map((lead) => [lead.id, false]),
    );
    // Merge with existing state instead of replacing
    setEmailRevealingLeads((prev) => ({
      ...prev,
      ...Object.fromEntries(updateLoadingEmailLeads),
    }));

    // email phone
    const updateLoadingEmailPhoneLeads = [];
    updateLoadingEmailPhoneLeads.push(
      ...revealingSearchEmailPhoneLeads.map((lead) => [lead.id, true]),
    );
    updateLoadingEmailPhoneLeads.push(
      ...revealedLeads.map((lead) => [lead.id, false]),
    );
    // Merge with existing state instead of replacing
    setEmailPhoneRevealingLeads((prev) => ({
      ...prev,
      ...Object.fromEntries(updateLoadingEmailPhoneLeads),
    }));
  };

  const handleFetchCounts = async (payload: any) => {
    try {
      setIsLoadingCounts(true);
      setCountsError(false);
      // Reset until we know this query is filtered (set below for verified users).
      showingFilteredCountsRef.current = false;
      // Skip API call if email is not verified and show dummy counts
      if (!isEmailVerified) {
        setCountsData((prevState) => ({
          ...prevState,
          totalCount: DEFAULT_TOTAL_LEADS_COUNT,
          netNewCount: DEFAULT_TOTAL_LEADS_COUNT,
        }));
        return;
      }
      // Remove pagination from payload for counts API
      const { page, start, take, ...countsPayload } = payload;
      // Default (no-filter) view: cache the total and derive netNew from the
      // saved count instead of hitting the expensive { tab: 'total' } query.
      // purgeCache forces a real refresh.
      const hasSearchFilters = Object.keys(countsPayload).length > 0;
      showingFilteredCountsRef.current = hasSearchFilters;
      const purgeCache =
        new URLSearchParams(window.location?.search).get('purgeCache') === '1';
      if (!hasSearchFilters && !purgeCache) {
        setCountsData((prevState) => ({
          ...prevState,
          totalCount: DEFAULT_TOTAL_LEADS_COUNT,
          netNewCount: deriveDefaultNetNew(
            prevState?.revealedCount,
            DEFAULT_TOTAL_LEADS_COUNT,
          ),
        }));
        return;
      }
      const res = await getLeadsV2Count(countsPayload);
      if (res?.payload) {
        setCountsData((prevState) => ({
          ...prevState,
          totalCount: res.payload.totalCount,
          netNewCount: res.payload.netNewCount,
        }));
      }
    } catch (e: any) {
      setCountsError(true);
      setCountsData((prevState) => ({
        ...prevState,
        totalCount: 0,
        netNewCount: 0,
      }));
      if (!e?.isHandled) {
        toaster.error(
          e.message || "Couldn't fetch lead counts. Please try again.",
        );
      }
    } finally {
      setIsLoadingCounts(false);
    }
  };

  const handleFetchSavedCounts = async (payload: any) => {
    // In the cached default view the saved count is the only value fetched, so
    // it owns the authoritative netNew = cachedTotal - savedCount.
    const savedParams = new URLSearchParams(window.location?.search);
    const savedPurgeCache = savedParams.get('purgeCache') === '1';
    ['page', 'start', 'take', 'purgeCache', 'tab'].forEach((k) =>
      savedParams.delete(k),
    );
    const isCachedDefaultView =
      isEmailVerified &&
      savedParams.toString().length === 0 &&
      !savedPurgeCache &&
      !skipUrlPushForBulkSearchRef?.current;
    // On the Saved tab handleFetchCounts is always called without filters
    // ({ page: 1 }), so it never fetches a real netNew — the saved-counts
    // response owns the netNew derivation there. Without this, refreshing the
    // Saved tab with an active filter leaves the "New" count stuck on its
    // loading skeleton: isCachedDefaultView is false (URL has filters) so
    // nothing ever sets netNewCount (BE-2529).
    // BUT when Total/New currently hold filtered counts (e.g. switching to
    // Saved after filtering), keep those filtered counts instead of resetting
    // netNew to the cached default (BE-2531).
    const ownsNetNewDerivation =
      (isCachedDefaultView || isSavedSubTab) &&
      !showingFilteredCountsRef.current;
    try {
      setIsLoadingSavedCounts(true);
      // Skip API call if email is not verified and show 0 for saved count
      if (!isEmailVerified) {
        setCountsData((prevState) => ({
          ...prevState,
          revealedCount: 0,
        }));
        return;
      }
      // Remove pagination params (start, take) from payload for counts API
      const { start, take, ...countsPayload } = payload;
      const res = await getSavedLeadsV2Count(countsPayload);
      if (res?.payload) {
        const revealed = (res.payload as any).revealedCount ?? 0;
        setCountsData((prevState) => ({
          ...prevState,
          revealedCount: revealed,
          ...(ownsNetNewDerivation
            ? {
                netNewCount: deriveDefaultNetNew(
                  revealed,
                  DEFAULT_TOTAL_LEADS_COUNT,
                ),
              }
            : {}),
        }));
      }
    } catch (e: any) {
      setCountsData((prevState) => ({
        ...prevState,
        revealedCount: 0,
        ...(ownsNetNewDerivation
          ? { netNewCount: DEFAULT_TOTAL_LEADS_COUNT }
          : {}),
      }));
      if (!e?.isHandled) {
        toaster.error(
          e.message || "Couldn't fetch saved lead counts. Please try again.",
        );
      }
    } finally {
      setIsLoadingSavedCounts(false);
    }
  };

  const handleFetchPeopleLists = useCallback(async (tab: LeadFinderListTab) => {
    try {
      setIsLoadingPeopleLists(true);
      const res = await getCompanyListsAPI(tab);
      if (res?.payload && Array.isArray(res.payload)) {
        // Transform API response to match AddToListData/SavedListData type
        const transformedLists = res.payload.map((list: any) => ({
          id: list.id,
          label: list.name,
          visibility: list.visibility as LeadFinderListVisibility,
          count: list.count,
          owner: {
            firstName: list.user?.firstName || '',
            lastName: list.user?.lastName || '',
          },
          isDefault: list.isDefault,
        }));
        setPeopleListsData(transformedLists);
      }
    } catch (e: any) {
      setPeopleListsData([]);
      if (!e?.isHandled) {
        toaster.error(
          e.message || "Couldn't fetch people lists. Please try again.",
        );
      }
    } finally {
      setIsLoadingPeopleLists(false);
    }
  }, []);

  const handleFetchCompanyLists = useCallback(
    async (tab: LeadFinderListTab) => {
      try {
        setIsLoadingCompanyLists(true);
        const res = await getCompanyListsAPI(tab);
        if (res?.payload && Array.isArray(res.payload)) {
          const transformedLists = res.payload.map((list: any) => ({
            id: list.id,
            label: list.name,
            visibility: list.visibility as LeadFinderListVisibility,
            count: list.count,
            owner: {
              firstName: list.user?.firstName || '',
              lastName: list.user?.lastName || '',
            },
            isDefault: list.isDefault,
          }));
          setCompanyListsData(transformedLists);
        }
      } catch (e: any) {
        setCompanyListsData([]);
        if (!e?.isHandled) {
          toaster.error(
            e.message || "Couldn't fetch company lists. Please try again.",
          );
        }
      } finally {
        setIsLoadingCompanyLists(false);
      }
    },
    [],
  );

  const handleFetchLeads = async (
    data: any,
    subTab: LeadFinderSecondaryTab,
  ) => {
    // eslint-disable-next-line no-plusplus
    const requestId = ++currentRequestIdRef.current;
    const requestKey = `${subTab}::${window.location?.search || ''}`;
    currentRequestKeyRef.current = requestKey;
    const isSaved = subTab === LeadFinderSecondaryTab.SAVED;
    try {
      setIsLoading(true);
      let res: any = {};
      if (!isSaved) {
        if (data && typeof data === 'object') {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { page, start, take, ...rest } = data;
          if (Object.keys(rest || {}).length > 0) {
            lastAppliedNonSavedFiltersRef.current = rest;
          }
        }
        if (aiSeachPopupSubmitted && aiSearchInputValue) {
          if (!isAiSearchInProgress) {
            dispatch(setIsAiSearchInProgress(true));
            try {
              res = await fetchAISearchResult({
                aiText: aiSearchInputValue,
                tab: activeSubTab,
              });
              if (res?.payload?.message) {
                toaster.success(res?.payload?.message);
              }
            } finally {
              dispatch(setIsAiSearchInProgress(false));
            }
            if (res?.payload?.filterSource?.toLowerCase() === 'system') {
              toaster.info(
                "We couldn't find an exact match. Try modifying your search",
                {
                  delay: 4000,
                  theme: Theme.New,
                  showCloseIcon: true,
                },
              );
            }
            saveAISearchToLocalStorage(aiSearchInputValue);
            dispatch(setAiSearchPopupInputValue(''));
          }
        } else {
          const params = new URLSearchParams();
          params.set('tab', activeSubTab);
          res = await getLeadsV2(params.toString(), data);
          const resolvedProfiles = Array.isArray(res?.payload)
            ? res?.payload
            : res?.payload?.profiles || [];
          populateRevealingLeadsStateAfterSearch(resolvedProfiles);
        }
      } else {
        res = await getSavedLeads(data);
        // Extract profiles for saved tab and populate revealing leads state
        const savedPayload = res?.payload || {};
        const savedProfiles = Array.isArray(savedPayload)
          ? savedPayload
          : savedPayload?.profiles || [];
        populateRevealingLeadsStateAfterSearch(savedProfiles);
      }
      if (res?.payload) {
        if (
          subTab !== activeSubTabRef.current ||
          requestId !== currentRequestIdRef.current ||
          requestKey !== currentRequestKeyRef.current
        ) {
          return;
        }
        if (aiSeachPopupSubmitted) {
          updateUrlWithAiSearchParams(res?.payload?.filters || {});
          // If backend returned pagination in AI response, use it as counts (no separate counts API call)
          const paginationFromAi = res?.payload?.pagination;
          if (paginationFromAi) {
            setCountsData((prev) => ({
              ...prev,
              totalCount: paginationFromAi?.totalCount ?? prev?.totalCount ?? 0,
              netNewCount:
                paginationFromAi?.netNewCount ?? prev?.netNewCount ?? 0,
            }));
          }
        }

        if (isSaved) {
          const savedPayload = res?.payload || {};
          const savedProfiles = Array.isArray(savedPayload)
            ? savedPayload
            : savedPayload?.profiles || [];
          const savedPaginationSource = Array.isArray(savedPayload)
            ? null
            : savedPayload?.pagination;

          const takeValue =
            savedPaginationSource?.take ||
            data?.take ||
            getV2SavedTabPageSize();
          const startValue = savedPaginationSource?.start || data?.start || 1;
          const pageNumber = Math.ceil(startValue / takeValue);

          const savedPagination = savedPaginationSource
            ? {
              page: pageNumber,
              limit: takeValue,
              totalRows: savedPaginationSource.total || 0,
            }
            : {
              page: pageNumber,
              limit: takeValue,
              // The leads response no longer carries a total. Seed from the
              // already-known saved count (correct on subsequent pages) or 0;
              // the central sync effect corrects it once the counts API resolves.
              totalRows: countsData?.revealedCount ?? 0,
            };

          // Always create a new array reference to ensure React detects changes
          // This is critical when the same leads are returned with updated data (like tags)
          setLeadsData({
            profiles: [...savedProfiles],
            pagination: savedPagination,
          });
        } else {
          const resolvedProfiles = Array.isArray(res?.payload)
            ? res?.payload
            : res?.payload?.profiles || [];
          const currentPage = data?.page ?? apiParam.page ?? 1;

          // If no profiles returned, set totalRows to 0 immediately
          // Otherwise use counts from state if available
          let totalRows = 0;
          if (resolvedProfiles.length === 0) {
            totalRows = 0;
          } else if (countsData) {
            if (subTab === LeadFinderSecondaryTab.TOTAL) {
              totalRows = countsData.totalCount || 0;
            } else if (subTab === LeadFinderSecondaryTab.NEW) {
              totalRows = countsData.netNewCount || 0;
            }
          }

          // Always create a new array reference to ensure React detects changes
          // This is critical when the same leads are returned with updated data (like tags)
          setLeadsData({
            profiles: [...resolvedProfiles],
            pagination: {
              page: currentPage,
              limit: LEADS_PER_PAGE,
              totalRows,
            },
          });
        }
      }
    } catch (e: any) {
      const emptyPagination = {
        page: 1,
        limit: isSaved ? getV2SavedTabPageSize() : LEADS_PER_PAGE,
        totalRows: 0,
      };

      if (e?.type === ExceptionTypes.RateLimit) {
        if (
          requestId === currentRequestIdRef.current &&
          subTab === activeSubTabRef.current &&
          requestKey === currentRequestKeyRef.current &&
          (!leadsData?.profiles?.length || !leadsData?.pagination?.totalRows)
        ) {
          setLeadsData({
            pagination: emptyPagination,
            profiles: [],
          });
        }
        return;
      }
      if (e?.isHandled) {
        if (
          requestId === currentRequestIdRef.current &&
          subTab === activeSubTabRef.current &&
          requestKey === currentRequestKeyRef.current
        ) {
          setLeadsData({
            pagination: emptyPagination,
            profiles: [],
          });
        }
        return;
      }
      if (!e?.isHandled) {
        toaster.error(e.message || "Couldn't fetch leads. Please try again.");
      }
    } finally {
      if (aiSeachPopupSubmitted) {
        // ** reset ai search popup submitted executed every time
        dispatch(setAiSearchPopupSubmitted(false));
      }
      if (
        requestId === currentRequestIdRef.current &&
        requestKey === currentRequestKeyRef.current
      ) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (
      !aiSeachPopupSubmitted ||
      !aiSearchInputValue ||
      isSavedSubTab ||
      shouldBlockAccess
    ) {
      return;
    }
    const fetchLeadsWithAiFilters = async () => {
      const payload = handleGenerateAPIParam(activeSubTab);
      if (payload?.page) {
        handleFetchLeads(null, activeSubTab);
      }
    };
    fetchLeadsWithAiFilters();
  }, [aiSeachPopupSubmitted, aiSearchInputValue, activeSubTab]);

  useEffect(() => {
    const switchedToPeopleTab =
      activeLeadType === LeadFinderPrimaryTab.PEOPLE &&
      previousActiveLeadTypeRef.current !== LeadFinderPrimaryTab.PEOPLE;

    // If the last URL update was from AI, skip this effect's API calls
    if (aiUrlUpdateRef.current) {
      aiUrlUpdateRef.current = false;
      previousSearchParamsRef.current = location.search;
      previousSubTabRef.current = activeSubTab;
      previousActiveLeadTypeRef.current = activeLeadType;
      return;
    }

    // Don't make API calls if user doesn't have access
    if (shouldBlockAccess || activeLeadType === LeadFinderPrimaryTab.COMPANY) {
      previousActiveLeadTypeRef.current = activeLeadType;
      return;
    }

    const pathSegments = location.pathname.split('/').filter(Boolean);
    const leadsIndex = pathSegments.findIndex((segment) => segment === 'leads');
    const subTabCandidate =
      leadsIndex !== -1 ? pathSegments[leadsIndex + 2] : undefined;
    const isValidSubTabCandidate =
      !!subTabCandidate &&
      Object.values(LeadFinderSecondaryTab).includes(
        subTabCandidate as LeadFinderSecondaryTab,
      );
    const pathnameSubTab = isValidSubTabCandidate
      ? (subTabCandidate as LeadFinderSecondaryTab)
      : null;

    if (pathnameSubTab && pathnameSubTab !== activeSubTab) {
      return;
    }

    const currentSearchParams = location.search;
    const previousSearchParams = previousSearchParamsRef.current;
    const previousSubTab = previousSubTabRef.current;

    const hasFilterParams = () => {
      const params = new URLSearchParams(currentSearchParams);
      params.delete('page');
      params.delete('start');
      params.delete('take');
      return params.toString().length > 0;
    };

    const isBulkSearchMode = !!skipUrlPushForBulkSearchRef?.current;
    const isBulkUrlClear =
      isBulkSearchMode &&
      !hasFilterParams() &&
      currentSearchParams !== previousSearchParams;
    const isBulkSavedToSearchSwitch =
      isBulkSearchMode &&
      previousSubTab === LeadFinderSecondaryTab.SAVED &&
      !!lastAppliedNonSavedFiltersRef.current &&
      Object.keys(lastAppliedNonSavedFiltersRef.current || {}).length > 0;

    if (!(switchedToPeopleTab && !hasFilterParams()) && !isBulkUrlClear) {
      currentRequestIdRef.current += 1;
      currentRequestKeyRef.current = `${activeSubTab}::${currentSearchParams || ''
        }`;
      setIsLoading(false);
    }

    // Only clear leads data when filters actually change (not on initial load, not during bulk URL clear)
    const isInitialLoad = previousSubTab === null;
    if (!isInitialLoad && !isBulkUrlClear && !isBulkSavedToSearchSwitch) {
      // Clear leads data when filters change
      setLeadsData({
        profiles: [],
        pagination: null,
      });
    }

    // Determine if we should call counts API
    const shouldCallCounts = () => {
      if (skipNextCountCallRef.current) {
        skipNextCountCallRef.current = false;
        return false;
      }
      if (isBulkSearchMode) {
        const currentParams = new URLSearchParams(currentSearchParams);
        currentParams.delete('page');
        currentParams.delete('start');
        currentParams.delete('take');
        const isCurrentUrlEmpty = currentParams.toString().length === 0;

        if (isCurrentUrlEmpty) {
          return false;
        }
      }

      // Always call on initial load
      if (previousSubTab === null) {
        return true;
      }

      // Call when switching to/from Saved tab
      const wasSavedTab = previousSubTab === LeadFinderSecondaryTab.SAVED;
      const isNowSavedTab = activeSubTab === LeadFinderSecondaryTab.SAVED;
      if (wasSavedTab !== isNowSavedTab) {
        return true;
      }

      // Don't call when switching between Total and New (filters persist, same URL)
      const isTotalToNewSwitch =
        (previousSubTab === LeadFinderSecondaryTab.TOTAL &&
          activeSubTab === LeadFinderSecondaryTab.NEW) ||
        (previousSubTab === LeadFinderSecondaryTab.NEW &&
          activeSubTab === LeadFinderSecondaryTab.TOTAL);
      if (isTotalToNewSwitch && currentSearchParams === previousSearchParams) {
        return false;
      }

      // Call when URL changes (search button click, clear all, saved filter, etc.)
      // Since filter selections no longer update URL (they update pendingFilters),
      // any URL change means user action
      if (currentSearchParams !== previousSearchParams) {
        return true;
      }

      // Call when switching back to people tab only if there are URL filters
      if (switchedToPeopleTab) {
        return hasFilterParams();
      }

      // For other cases, don't auto-trigger
      return false;
    };

    const fetchLeads = async () => {
      if (isSavedSubTab) {
        // Reset previousSavedFiltersRef when switching back to saved tab
        // This ensures filters are always synced from URL when returning to saved tab
        const wasSavedTab = previousSubTab === LeadFinderSecondaryTab.SAVED;
        if (!wasSavedTab) {
          previousSavedFiltersRef.current = null;
        }

        // Reset pagination when switching to saved tab
        const resetPagination = {
          start: 1,
          take: getV2SavedTabPageSize(),
        };
        setSavedApiParam(resetPagination);

        // Only use saved tab filters (keyword, tags, createdDate)
        // Ignore any other filters that might be in URL from previous tab
        const payload: any = {
          ...resetPagination,
        };
        const savedPayload = handleGeneratePayload();
        // Only include saved tab specific filters
        if (savedPayload.search) {
          payload.search = Array.isArray(savedPayload?.search)
            ? savedPayload?.search?.[0]
            : savedPayload?.search;
        }
        if (savedPayload.tags) {
          payload.tags = savedPayload.tags;
        }
        if (savedPayload.createdDate) {
          payload.createdDate = savedPayload.createdDate;
        }
        if (savedPayload.excludeProspects) {
          payload.excludeProspects = savedPayload.excludeProspects;
        }
        if (savedPayload.emailStatus?.length) {
          payload.emailStatus = savedPayload.emailStatus;
        }

        const queryParamsUpdated = new URLSearchParams(window.location?.search);
        const peopleListIncludesStr = queryParamsUpdated.get('people_list');
        const peopleListExcludesStr = queryParamsUpdated.get(
          'excludePeople_list',
        );
        const listIds: any = {};

        if (peopleListIncludesStr) {
          const includes = peopleListIncludesStr
            .split(',')
            .map((id) => parseInt(id.replace(/"/g, '').trim(), 10))
            .filter((id) => !Number.isNaN(id) && id > 0);
          if (includes.length > 0) {
            listIds.includes = includes;
          }
        }

        if (peopleListExcludesStr) {
          const excludes = peopleListExcludesStr
            .split(',')
            .map((id) => parseInt(id.replace(/"/g, '').trim(), 10))
            .filter((id) => !Number.isNaN(id) && id > 0);
          if (excludes.length > 0) {
            listIds.excludes = excludes;
          }
        }

        // Convert to simple array format (like tags) - only use includes
        if (listIds.includes && listIds.includes.length > 0) {
          payload.listIds = listIds.includes;
        }

        const savedCountPayload = {
          ...payload,
          tags: savedPayload.tags?.split(',').map((tag) => Number(tag.trim())),
          createdDate: savedPayload.createdDate
            ?.split(',')
            .map((date) =>
              dateToString(new Date(decodeURIComponent(date.trim()))),
            ),
          listIds: Object.keys(listIds).length > 0 ? listIds : undefined,
        };
        // Call saved counts API (always call, even without filters for initial load)
        handleFetchSavedCounts(savedCountPayload);

        const hasTotalCount = countsDataRef.current?.totalCount !== undefined;
        const hasNetNewCount = countsDataRef.current?.netNewCount !== undefined;

        if (!hasTotalCount || !hasNetNewCount) {
          handleFetchCounts({ page: 1 });
        }

        // Call saved leads API
        handleFetchLeads(payload, activeSubTab);
        if (
          previousSubTab === null &&
          activeLeadType === LeadFinderPrimaryTab.PEOPLE
        ) {
          handleFetchPeopleLists(LeadFinderListTab.People);
          handleFetchCompanyLists(LeadFinderListTab.Company);
        }
      } else {
        // Reset pagination when switching to non-saved tab
        const resetPagination = {
          page: 1,
        };
        setApiParam(resetPagination);

        // In bulk-search mode with no URL params, use the preserved filters from the
        // last search so Total/New tab switches re-fetch with the same filters.
        const filtersPayload =
          isBulkSearchMode &&
            !hasFilterParams() &&
            lastAppliedNonSavedFiltersRef.current
            ? lastAppliedNonSavedFiltersRef.current
            : handleGeneratePayload();
        const payload = {
          ...resetPagination,
          ...filtersPayload,
        };

        // Call counts API in parallel if needed (only for non-saved tabs)
        const needsCounts = shouldCallCounts();
        if (needsCounts) {
          // Call counts API in parallel (without page parameter)
          handleFetchCounts(payload);
        }

        // Call saved counts API on initial load and whenever we (re-)enter the
        // cached default view (e.g. "Clear all"), so netNew = cachedTotal - saved
        // stays fresh. Skipped when filters are applied or in bulk-search mode.
        const isDefaultNoFilterView =
          isEmailVerified && !hasFilterParams() && !isBulkSearchMode;
        if (
          previousSubTab === null ||
          previousSubTab === LeadFinderSecondaryTab.SAVED ||
          isDefaultNoFilterView
        ) {
          handleFetchSavedCounts({});
        }

        if (
          previousSubTab === null &&
          activeLeadType === LeadFinderPrimaryTab.PEOPLE
        ) {
          handleFetchPeopleLists(LeadFinderListTab.People);
          handleFetchCompanyLists(LeadFinderListTab.Company);
        }

        // Only call search API if:
        // 1. Initial load with filter params in URL
        // 2. Tab switch between Total/New with filters
        // 3. URL change with filters (search button click, saved filter, etc.)
        // 4. Switched back to people tab with filters (URL restored from state)
        const { page, ...filters } = payload;
        const isTotalToNewSwitch =
          (previousSubTab === LeadFinderSecondaryTab.TOTAL &&
            activeSubTab === LeadFinderSecondaryTab.NEW) ||
          (previousSubTab === LeadFinderSecondaryTab.NEW &&
            activeSubTab === LeadFinderSecondaryTab.TOTAL);
        const isBulkSwitchSearch =
          isBulkSearchMode &&
          isTotalToNewSwitch &&
          !!lastAppliedNonSavedFiltersRef.current &&
          Object.keys(lastAppliedNonSavedFiltersRef.current || {}).length > 0;
        const shouldCallSearch =
          (previousSubTab === null && hasFilterParams()) || // Initial load with params
          (isTotalToNewSwitch && hasFilterParams()) || // Tab switch with filters
          (currentSearchParams !== previousSearchParams && hasFilterParams()) || // URL change with filters
          (switchedToPeopleTab && hasFilterParams()) || // Switched back to people tab with filters
          isBulkSwitchSearch ||
          isBulkSavedToSearchSwitch;

        if (Object.keys(filters || {})?.length && shouldCallSearch) {
          handleFetchLeads(payload, activeSubTab);
        }
      }
    };

    fetchLeads();

    // Update refs
    previousSearchParamsRef.current = currentSearchParams;
    previousSubTabRef.current = activeSubTab;
    previousActiveLeadTypeRef.current = activeLeadType;
  }, [activeSubTab, location.search, activeLeadType]);

  useEffect(() => {
    countsDataRef.current = countsData;
  }, [countsData]);

  useEffect(() => {
    activeSubTabRef.current = activeSubTab;
  }, [activeSubTab]);

  const searchLeads = (payload?: any, performSearch = true) => {
    const isSaved = activeSubTab === LeadFinderSecondaryTab.SAVED;

    // Reset pagination when filters change (when payload is provided)
    if (payload) {
      const resetPagination = isSaved
        ? {
          start: 1,
          take: getV2SavedTabPageSize(),
        }
        : {
          page: 1,
        };

      const finalPayload = {
        ...resetPagination,
        ...payload,
      };

      (isSaved ? setSavedApiParam : setApiParam)(resetPagination);
      if (performSearch) {
        handleFetchLeads(finalPayload, activeSubTab);
      }
      return;
    }
    const updatedPayload = handleGenerateAPIParam(activeSubTab);
    if (performSearch) {
      handleFetchLeads(updatedPayload, activeSubTab);
    }
  };

  const handlePagination = ({
    page,
    limit,
    filterSource,
  }: {
    page: number;
    limit?: number;
    filterSource?: string;
  }) => {
    if (activeSubTab === LeadFinderSecondaryTab.SAVED) {
      const takeValue = limit || savedApiParam.take || getV2SavedTabPageSize();
      const startValue = (page - 1) * takeValue + 1;
      // Update state for future reference
      setSavedApiParam({
        start: startValue,
        take: takeValue,
      });
      // Construct payload directly with new pagination values to avoid stale state
      const filtersPayload = handleGeneratePayload(filterSource);
      const updatedPayload = {
        start: startValue,
        take: takeValue,
        ...filtersPayload,
      };
      handleFetchLeads(updatedPayload, activeSubTab);
    } else {
      // Update state for future reference
      setApiParam({
        page,
      });
      // Construct payload directly with new pagination values to avoid stale state
      let filtersPayload = handleGeneratePayload(filterSource);

      if (
        skipUrlPushForBulkSearchRef?.current &&
        Object.keys(filtersPayload || {}).length === 0 &&
        lastAppliedNonSavedFiltersRef.current
      ) {
        filtersPayload = lastAppliedNonSavedFiltersRef.current;
      }
      const updatedPayload = {
        page,
        ...filtersPayload,
      };
      handleFetchLeads(updatedPayload, activeSubTab);
    }
  };

  useEffect(() => {
    if (location?.search) {
      handleGenerateAPIParam(activeSubTab);
      setUrlState({
        payload: handleGeneratePayload(),
        url: location?.pathname + location?.search,
      });
    } else if (!skipUrlPushForBulkSearchRef?.current) {
      setLeadsData({
        profiles: [],
        pagination: null,
      });
    }
  }, [location.search, activeSubTab]);

  // Update pagination totalRows from countsData when counts or profiles change.
  // Non-saved tabs use totalCount/netNewCount; the saved tab uses revealedCount
  // (from /v2/leads/counts {tab:'saved'}) since the leads response no longer
  // carries a pagination total.
  useEffect(() => {
    if (!countsData) {
      return;
    }
    setLeadsData((prevState) => {
      if (!prevState?.pagination) {
        return prevState;
      }

      // If no profiles, total is 0
      if (prevState?.profiles?.length === 0) {
        if (prevState.pagination.totalRows !== 0) {
          return {
            ...prevState,
            pagination: { ...prevState.pagination, totalRows: 0 },
          };
        }
        return prevState;
      }

      // Profiles present: derive total from the relevant count for this tab
      let { totalRows } = prevState.pagination;
      if (isSavedSubTab) {
        totalRows = countsData.revealedCount || 0;
      } else if (activeSubTab === LeadFinderSecondaryTab.TOTAL) {
        totalRows = countsData.totalCount || 0;
      } else if (activeSubTab === LeadFinderSecondaryTab.NEW) {
        totalRows = countsData.netNewCount || 0;
      }

      if (prevState.pagination.totalRows !== totalRows) {
        return {
          ...prevState,
          pagination: { ...prevState.pagination, totalRows },
        };
      }
      return prevState;
    });
  }, [
    countsData,
    activeSubTab,
    isSavedSubTab,
    leadsData?.profiles?.length,
    leadsData?.pagination?.totalRows,
  ]);

  useEffect(
    () => () => {
      if (!urlState || window.location?.pathname === '/leads') {
        return; // If urlState is not set, do nothing
      }

      if (isSavedSubTab) {
        return;
      }

      // Check if current URL matches the stored urlState
      // If they don't match, the URL has changed (filters cleared/modified), so don't save stale data
      const currentUrl = window.location.pathname + window.location.search;
      if (currentUrl.toLowerCase() !== urlState?.url?.toLowerCase()) {
        return; // URL has changed, don't save stale state
      }

      const { payload } = urlState;
      const meaningfulParams = Object.keys(payload)?.filter(
        (key) => key !== 'start' && key !== 'take' && key !== 'page',
      );

      if (meaningfulParams.length > 0 && userEmail) {
        const storedUrlsKey = V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY;
        const storedUrls = JSON.parse(
          StorageUtil.getItem(storedUrlsKey) || '{}',
        );

        if (!storedUrls[userEmail]) {
          storedUrls[userEmail] = [];
        }

        const timestamp = new Date().toISOString();

        const existingUrlIndex = storedUrls[userEmail].findIndex(
          (urlData) =>
            urlData?.url?.toLowerCase() === urlState?.url?.toLowerCase(),
        );

        const maxUrls = 4;

        const urlSearch = urlState?.url?.includes('?')
          ? urlState.url.slice(urlState.url.indexOf('?') + 1)
          : '';
        const displayPayload = buildRecentSearchDisplayPayload(
          payload,
          new URLSearchParams(urlSearch),
          false,
        );

        if (existingUrlIndex !== -1) {
          const updatedEntry = {
            url: urlState?.url,
            timestamp,
            payload,
            displayPayload,
          };

          storedUrls[userEmail].unshift(
            storedUrls[userEmail].splice(existingUrlIndex, 1)[0],
          );
          storedUrls[userEmail][0] = updatedEntry;

          storedUrls[userEmail] = storedUrls[userEmail].slice(0, maxUrls);
        } else {
          const urlData = {
            url: urlState?.url,
            timestamp,
            payload,
            displayPayload,
          };

          storedUrls[userEmail].unshift(urlData);

          storedUrls[userEmail] = storedUrls[userEmail].slice(0, maxUrls);
        }

        StorageUtil.setItem(storedUrlsKey, JSON.stringify(storedUrls));
      }
    },
    [urlState],
  );

  const refreshPeopleLists = useCallback(() => {
    handleFetchPeopleLists(LeadFinderListTab.People);
  }, [handleFetchPeopleLists]);

  return {
    isLoading,
    isLoadingCounts,
    isLoadingSavedCounts,
    leadsData,
    apiParam,
    savedApiParam,
    handlePagination,
    handleRecentSearch,
    userEmail,
    handleGeneratePayload,
    handleAppendLead,
    setLeadsData,
    handleRouteChange,
    searchLeads,
    handleFetchCounts,
    populateRevealingLeadsStateAfterSearch,
    countsData,
    countsError,
    peopleListsData,
    companyListsData,
    isLoadingPeopleLists,
    isLoadingCompanyLists,
    refreshPeopleLists,
    refreshCompanyLists: () =>
      handleFetchCompanyLists(LeadFinderListTab.Company),
    skipNextCountCallRef,
    handleFetchSavedCounts,
  };
};
